import React, { useState } from 'react';
import { MessageSquare, Activity, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription } from 'components/ui/alert';
import { cn } from 'lib/utils';

interface EirLoadOptionsModalProps {
  isOpen: boolean;
  onChatWithAI: () => void;
  onHealthAnalysis: () => void;
  onCancel: () => void;
  isLoadingAnalysis?: boolean;
}

export const EirLoadOptionsModal: React.FC<EirLoadOptionsModalProps> = ({
  isOpen,
  onChatWithAI,
  onHealthAnalysis,
  onCancel,
  isLoadingAnalysis = false,
}) => {
  const [isMobilePrivacyExpanded, setIsMobilePrivacyExpanded] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent
        className="max-w-xl"
        aria-labelledby="eir-load-modal-title"
      >
        <DialogHeader className="text-left space-y-1.5">
          <DialogTitle id="eir-load-modal-title">
            What would you like to do?
          </DialogTitle>
          <DialogDescription>
            Your health journal has been loaded. Choose how you&apos;d like to explore it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <button
 type="button"
 onClick={onChatWithAI}
 disabled={isLoadingAnalysis}
 className={cn(
 'border rounded-xl p-5 flex items-center gap-4 cursor-pointer hover:bg-accent transition-colors text-left w-full',
 'disabled:opacity-60 disabled:cursor-not-allowed'
 )}
 >
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary text-primary-foreground shrink-0">
              <MessageSquare size={24} />
            </div>
            <div className="flex-1 min-w-0 grid gap-1">
              <h4 className="m-0 text-base font-semibold text-foreground-strong">
                Chat about your health with AI
              </h4>
              <p className="m-0 text-sm text-foreground-muted leading-normal">
                Have a conversation with AI about your health history, get insights, and ask questions.
              </p>
            </div>
          </button>

          <button
 type="button"
 onClick={onHealthAnalysis}
 disabled={isLoadingAnalysis}
 className={cn(
 'border rounded-xl p-5 flex items-center gap-4 cursor-pointer hover:bg-accent transition-colors text-left w-full',
 'disabled:opacity-60 disabled:cursor-not-allowed'
 )}
 >
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary text-primary-foreground shrink-0">
              <Activity size={24} />
            </div>
            <div className="flex-1 min-w-0 grid gap-1">
              <h4 className="m-0 text-base font-semibold text-foreground-strong">
                Generate Health Analysis
              </h4>
              <p className="m-0 text-sm text-foreground-muted leading-normal">
                Get a comprehensive health summary with scores, key events, and recommendations.
              </p>
            </div>
          </button>
        </div>

        <Alert
          className={cn(
            'flex items-start gap-3 p-4 rounded-lg',
            'bg-amber-50 border-amber-200 text-amber-900'
          )}
        >
          <AlertCircle size={18} className="shrink-0 mt-0.5 text-amber-600" />
          <div className="flex-1 min-w-0 w-full">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <strong className="text-amber-900">Privacy Notice</strong>
              <button
 type="button"
 onClick={() => setIsMobilePrivacyExpanded((prev) => !prev)}
                className={cn(
                  'hidden max-sm:inline-flex items-center gap-1 font-semibold text-sm text-amber-800',
                  'hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-400 rounded'
                )}
              >
                {isMobilePrivacyExpanded ? 'Hide details' : 'View details'}
              </button>
            </div>
            <AlertDescription
              className={cn(
                'text-sm text-amber-800 leading-relaxed mt-1 [&_p]:mb-3 [&_p:last-child]:mb-0',
                'max-sm:block',
                !isMobilePrivacyExpanded && 'max-sm:hidden'
              )}
            >
              <p>Both options send your health data to our remote server for processing.</p>
              <p>
                <strong>Zero Data Retention (ZDR) Policy:</strong> We maintain a strict Zero Data Retention policy - your
                health data is processed but never permanently stored on our servers. Our cloud provider (Groq) also
                follows a Zero Data Retention policy, ensuring your information is deleted immediately after processing.
              </p>
            </AlertDescription>
          </div>
        </Alert>

        <DialogFooter className="flex justify-end">
          <Button type="button" onClick={onCancel} disabled={isLoadingAnalysis}>
            Maybe later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
